import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';

const PersonalEducation = ({ 
  userEducation, 
  setUserEducation, 
  educationLoading, 
  setEducationLoading,
  getCachedEducation,
  onEditEducation,
  onRemoveEducation,
  onShowAllEducation,
  onAddEducation
}) => {

  // Load cached data first for instant display
  useEffect(() => {
    const cachedEducation = getCachedEducation();
    if (cachedEducation.length > 0) {
      setUserEducation(cachedEducation);
    }
    // Then fetch fresh data in background
    fetchUserEducation();
  }, [getCachedEducation]);

  const fetchUserEducation = async () => {
    setEducationLoading(true);
    try {
      const response = await ApiService.get('/education');
      if (response.success) {
        setUserEducation(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching user education:', error);
    } finally {
      setEducationLoading(false);
    }
  };

  // Format date range with styled Present badge
  const formatDateRangeWithBadge = (startDate, endDate, isCurrent) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
    };

    const start = formatDate(startDate);
    
    if (isCurrent) {
      return (
        <>
          {start} - <span className="present-badge">Present</span>
        </>
      );
    } else {
      const end = formatDate(endDate);
      return `${start} - ${end}`;
    }
  };

  return (
    <div className="personal-education-section">
      <div className="section-header">
        <h3>Education</h3>
        <button 
          className="header-btn add-education-btn"
          onClick={onAddEducation}
          title="Add Education"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Education
        </button>
      </div>

      {educationLoading ? (
        <div className="education-loading">
          <div className="education-card-shimmer">
            <div className="education-header-shimmer"></div>
            <div className="education-content-shimmer"></div>
          </div>
        </div>
      ) : userEducation.length > 0 ? (
        <div className="education-list">
          {userEducation.slice(0, 3).map((education) => (
            <div key={education.id} className="education-card">
              <div className="education-header">
                <div className="education-title-section">
                  {education.institution_logo && (
                    <img 
                      src={education.institution_logo} 
                      alt={`${education.institution} logo`}
                      className="institution-logo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="education-title-content">
                    <h4 className="education-title">
                      {education.degree}
                      {education.field_of_study && (
                        <span className="degree-field"> ({education.field_of_study})</span>
                      )}
                    </h4>
                    <p className="education-institution">{education.institution}</p>
                  </div>
                </div>
                <div className="education-actions">
                  <button
                    className="edit-education-btn"
                    onClick={() => onEditEducation(education)}
                    title="Edit education"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                    </svg>
                  </button>
                  <button
                    className="remove-education-btn"
                    onClick={() => onRemoveEducation(education.id)}
                    title="Remove education"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3,6 5,6 21,6"></polyline>
                      <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="education-details">
                <div className="education-meta">
                  <div className="education-date-location-row">
                    <div className="education-dates">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12,6 12,12 16,14"></polyline>
                      </svg>
                      <span>
                        {formatDateRangeWithBadge(education.start_date, education.end_date, education.is_current)}
                      </span>
                    </div>
                    
                    {education.location && (
                      <div className="education-location">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        <span>{education.location}</span>
                      </div>
                    )}
                  </div>

                </div>


                {education.achievements && education.achievements.length > 0 && (
                  <div className="education-achievements">
                    <h5>Achievements:</h5>
                    <ul>
                      {education.achievements.map((achievement, index) => (
                        <li key={index}>{achievement}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}

          {userEducation.length > 3 && (
            <button 
              className="view-all-btn"
              onClick={onShowAllEducation}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3h18v18H3zM9 9h6v6H9z"></path>
                <path d="M9 3v6M15 3v6M9 15v6M15 15v6"></path>
              </svg>
              View All Education ({userEducation.length})
            </button>
          )}
        </div>
      ) : (
        <div className="no-education-state">
          <div className="no-education-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
              <path d="M8 7h8"></path>
              <path d="M8 11h8"></path>
              <path d="M8 15h5"></path>
            </svg>
          </div>
          <div className="no-education-content">
            <h4>Build Your Academic Profile</h4>
            <p>Showcase your educational background and academic achievements to strengthen your professional profile</p>
            <div className="no-education-features">
              <div className="feature-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                </svg>
                <span>Academic achievements</span>
              </div>
              <div className="feature-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                </svg>
                <span>Degree details</span>
              </div>
              <div className="feature-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>Institution information</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalEducation;
